/**
 * Multi-seat presentation shared by the canvas cards and the SVG export.
 * The people projection writes `roles` (every seat, primary first) and
 * `roleCount` on a person node; a hand-authored card has neither.
 */
export interface PersonRoleAttr {
  readonly positionId?: string;
  readonly title?: string;
  readonly department?: string;
  readonly fte?: number | string;
  readonly primary?: boolean;
}

export const ROLES_CHIP_HEIGHT = 16;
export const ROLES_CHIP_FONT_SIZE = 10;
export const ROLES_CHIP_INSET = 8;
export const ROLES_CHIP_FILL = '#EEF2FF';
export const ROLES_CHIP_STROKE = '#C7D2FE';
export const ROLES_CHIP_TEXT = '#3730A3';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export function readPersonRoles(attrs: Readonly<Record<string, unknown>>): PersonRoleAttr[] {
  const roles = attrs.roles;
  if (!Array.isArray(roles)) return [];
  return roles.filter(isRecord).map((role) => ({
    positionId: typeof role.positionId === 'string' ? role.positionId : undefined,
    title: typeof role.title === 'string' ? role.title : undefined,
    department: typeof role.department === 'string' ? role.department : undefined,
    fte: typeof role.fte === 'number' || typeof role.fte === 'string' ? role.fte : undefined,
    primary: role.primary === true,
  }));
}

/** Seats beyond the one the card is placed at. */
export function extraRoleCount(attrs: Readonly<Record<string, unknown>>): number {
  const fromRoles = readPersonRoles(attrs).length;
  const rawCount = Number(attrs.roleCount);
  const fromCount = Number.isFinite(rawCount) ? Math.round(rawCount) : 0;
  return Math.max(0, Math.max(fromRoles, fromCount) - 1);
}

export function rolesChipLabel(extra: number): string {
  return `+${extra} role${extra === 1 ? '' : 's'}`;
}

export function rolesChipWidth(extra: number): number {
  return Math.round(rolesChipLabel(extra).length * ROLES_CHIP_FONT_SIZE * 0.62 + 14);
}

/** Horizontal room the chip takes from a text line sharing its corner. */
export function rolesChipReserve(attrs: Readonly<Record<string, unknown>>): number {
  const extra = extraRoleCount(attrs);
  return extra === 0 ? 0 : rolesChipWidth(extra) + ROLES_CHIP_INSET;
}

export function roleTitles(attrs: Readonly<Record<string, unknown>>): string[] {
  return readPersonRoles(attrs)
    .map((role) => role.title?.trim() ?? '')
    .filter((title) => title.length > 0);
}
